import React from 'react'
import cn from 'classnames'
import styles from '../field.module.scss'
import { useController, useFormContext } from 'react-hook-form'
import { handleChangeOptionsCheckboxes } from '../../../../lib/functions/webform_fields_functions/webform_fields_functions'
import { CheckboxesProps } from '../../../../lib/types/components/checkboxes'
import {
  getClassNames,
  getDataAttributes,
} from '../../../../lib/functions/utils_functions'

const Checkboxes = ({
  fieldKey,
  field,
  className,
  innerProps,
  itemProps,
  inputProps,
  labelProps,
  ariaDescribedBy,
  classNamePrefix,
  unstyled,
  innerRef,
  validationEngine,
  onChange: onChangeProp,
  onBlur: onBlurProp,
  onFocus: onFocusProp,
}: CheckboxesProps) => {
  const { control } = useFormContext()

  if (!field?.['#options']) return null

  const optionsObj = Object.entries(field['#options'] as Record<string, string>)

  const { field: fieldController } = useController<any>({
    name: fieldKey,
    control,
  })

  const wrapperClassNames = getClassNames({
    name: 'checkboxesWrapper',
    prefix: classNamePrefix,
    unstyled: unstyled,
    classNameComponent: className,
    baseCn: cn(styles.checkboxes),
  })

  const itemClassNames = getClassNames({
    name: 'checkboxesItem',
    prefix: classNamePrefix,
    unstyled: unstyled,
    baseCn: styles.checkbox,
  })

  const inputClassNames = getClassNames({
    name: 'checkboxesInput',
    prefix: classNamePrefix,
    unstyled: unstyled,
    baseCn: styles.field,
  })

  const labelClassNames = getClassNames({
    name: 'checkboxesLabel',
    prefix: classNamePrefix,
    unstyled: unstyled,
    baseCn: styles.labelCheckbox,
  })

  const dataAttributes = getDataAttributes({
    component: 'Checkboxes',
  })

  const handleChange = (option: string, checked: boolean) => {
    const nextValue: string[] = Array.isArray(fieldController.value)
      ? [...fieldController.value]
      : []

    if (checked && !nextValue.includes(option)) {
      nextValue.push(option)
    } else if (!checked) {
      const index = nextValue.indexOf(option)
      if (index > -1) nextValue.splice(index, 1)
    }

    handleChangeOptionsCheckboxes(option, checked, fieldController)

    onChangeProp?.(nextValue)
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    fieldController.onBlur()
    onBlurProp?.(e)
  }

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    onFocusProp?.(e)
  }

  return (
    <div
      className={wrapperClassNames}
      ref={innerRef}
      role="group"
      {...dataAttributes}
      {...innerProps}
    >
      {optionsObj.map(([optionKey, optionValue], i) => {
        const checked = Array.isArray(fieldController.value)
          ? fieldController.value.includes(optionKey)
          : false

        const id = `checkboxes-${optionKey.trim()}-${i}`

        const isHtmlNativeRequired =
          validationEngine === 'html' &&
          field?.['#required'] &&
          (!Array.isArray(fieldController.value) ||
            fieldController.value.length === 0)

        return (
          <div key={optionKey} className={itemClassNames} {...itemProps}>
            <input
              id={id}
              className={inputClassNames}
              name={fieldController.name}
              type="checkbox"
              value={optionKey}
              checked={checked}
              aria-describedby={ariaDescribedBy}
              required={isHtmlNativeRequired}
              onChange={(e) => {
                handleChange(e.target.value, e.target.checked)
              }}
              onBlur={handleBlur}
              onFocus={handleFocus}
              {...inputProps}
            />

            <label htmlFor={id} className={labelClassNames} {...labelProps}>
              {optionValue}
            </label>
          </div>
        )
      })}
    </div>
  )
}

export default React.memo(Checkboxes)
